import { Button, Group, List, Paper, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';

import { Select } from './Select';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectAddToListProps {
  options: SelectOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  disabled?: boolean;
  withAsterisk?: boolean;
  description?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  readonly?: boolean;
}

export const SelectAddToList = ({
  options,
  value = [],
  onChange,
  label = 'Select an item...',
  disabled = false,
  withAsterisk,
  description,
  error,
  required,
  size = 'sm',
  readonly = false,
}: SelectAddToListProps) => {
  const [currentSelection, setCurrentSelection] = useState<string>('');

  const selectedValues: string[] = value || [];

  const selectedItems = selectedValues.map((val) => options.find((option) => option.value === val)).filter(Boolean) as SelectOption[];

  const sortedSelectedItems = selectedItems.sort((a, b) => a.label.localeCompare(b.label));

  const availableOptions = options.filter((option) => !selectedValues.includes(option.value));

  const handleAddItem = (valueToAdd?: string) => {
    const itemValue = valueToAdd || currentSelection;
    if (itemValue && !selectedValues.includes(itemValue)) {
      const newValues = [...selectedValues, itemValue];
      onChange?.(newValues);
      setCurrentSelection('');
    }
  };

  const handleSelectionChange = (selectedValue: string | null) => {
    if (selectedValue) {
      handleAddItem(selectedValue);
    } else {
      setCurrentSelection('');
    }
  };

  const handleRemoveItem = (valueToRemove: string) => {
    const newValues = selectedValues.filter((val) => val !== valueToRemove);
    onChange?.(newValues);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && currentSelection) {
      event.preventDefault();
      handleAddItem();
    }
  };

  return (
    <Stack gap="md">
      <Group>
        {!readonly && (
          <Select
            label={label}
            data={availableOptions}
            value={currentSelection}
            onChange={handleSelectionChange}
            disabled={disabled}
            style={{ flex: 1 }}
            searchable
            onKeyDown={handleKeyDown}
            withAsterisk={withAsterisk || required}
            description={description}
            error={error}
            size={size}
          />
        )}
      </Group>

      {sortedSelectedItems.length > 0 && (
        <Paper withBorder p="md">
          <Text size="sm" fw={500} mb="sm">
            Selected Items ({sortedSelectedItems.length})
          </Text>

          <List>
            {sortedSelectedItems.map((item) => (
              <List.Item key={item.value}>
                <Group justify="space-between">
                  <Text>{item.label}</Text>
                  {!readonly && (
                    <Button size="xs" variant="subtle" color="red" onClick={() => handleRemoveItem(item.value)} disabled={disabled}>
                      <IconX size={12} />
                    </Button>
                  )}
                </Group>
              </List.Item>
            ))}
          </List>
        </Paper>
      )}
    </Stack>
  );
};
